/**
 * BarcaChatProvider - Configuration provider for the Barca Chat Widget
 * Provides configuration context to all child components
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { BarcaChatConfig } from '../types';

interface BarcaChatContextType {
  config: BarcaChatConfig;
}

const BarcaChatContext = createContext<BarcaChatContextType | undefined>(undefined);

interface BarcaChatProviderProps {
  config: BarcaChatConfig;
  children: ReactNode;
}

/**
 * Provider component that supplies configuration to all Barca Chat components
 * @param config - The configuration object for the chat widget
 * @param children - Child components that will have access to the configuration
 */
export const BarcaChatProvider: React.FC<BarcaChatProviderProps> = ({ config, children }) => {
  // Validate required configuration
  if (!config.agentId) {
    throw new Error('BarcaChat: agentId is required in configuration');
  }

  // Set default values
  const configWithDefaults: BarcaChatConfig = {
    hideOnTextSelection: true,
    position: 'bottom-right',
    textChatLabel: 'Text Chat',
    textChatDescription: 'Type your messages',
    ...config,
  };

  return (
    <BarcaChatContext.Provider value={{ config: configWithDefaults }}>
      {children}
    </BarcaChatContext.Provider>
  );
};

/**
 * Hook to access the Barca Chat configuration
 * Must be used within a BarcaChatProvider
 */
export const useBarcaChatConfig = (): BarcaChatContextType => {
  const context = useContext(BarcaChatContext);
  if (context === undefined) {
    throw new Error('useBarcaChatConfig must be used within a BarcaChatProvider');
  }
  return context;
};
